import { useState, useCallback } from "react";
import {
  API_BASE_URL,
  getCachedPartners,
  getCachedGuarantees,
  setCachedPartners,
  setCachedGuarantees,
} from "@/mocks/adminData";
import type { Partner, Guarantee } from "@/mocks/adminData";

export interface VerifyResult {
  status: "success" | "fail" | "error";
  message: string;
  source: "api" | "cache";
  data?: {
    id: string;
    name?: string;
    remark?: string;
  };
}

// ---- 超时 fetch（公开API不需要 credentials）----
function fetchWithTimeout(url: string, ms: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return fetch(url, {
    signal: controller.signal,
    method: "GET",
    headers: { "Accept": "application/json" },
    // 注意：公开API用 * 允许所有来源，不能同时设 credentials: include
    // 所以这里不设 credentials，让浏览器用默认的 same-origin
  }).finally(() => clearTimeout(timer));
}

// ---- 直接调用后端 API 验证（最权威）----
async function verifyViaAPI(id: string): Promise<VerifyResult | null> {
  try {
    const res = await fetchWithTimeout(
      `${API_BASE_URL}/api/guarantee/verify?id=${encodeURIComponent(id)}`,
      8000
    );
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    if (!data) return null;

    if (data.code === 200 && data.data) {
      return {
        status: "success",
        source: "api",
        message: "验证通过！该ID为官方认证合作伙伴，放心合作。",
        data: {
          id: data.data.id,
          name: data.data.name,
          remark: data.data.remark,
        },
      };
    }
    if (data.code === 404) {
      return {
        status: "fail",
        source: "api",
        message: "验证失败！该ID不在官方认证列表中，请谨慎辨别，防止被骗。",
      };
    }
    return null;
  } catch {
    return null;
  }
}

// ---- 后台静默拉取最新数据并缓存 ----
async function refreshCache(): Promise<void> {
  try {
    const [partnersRes, guaranteesRes] = await Promise.allSettled([
      fetchWithTimeout(`${API_BASE_URL}/api/partners/list`, 6000),
      fetchWithTimeout(`${API_BASE_URL}/api/guarantee/list`, 6000),
    ]);

    if (partnersRes.status === "fulfilled" && partnersRes.value.ok) {
      const d = await partnersRes.value.json().catch(() => null);
      if (d?.code === 200 && Array.isArray(d.data) && d.data.length > 0) {
        setCachedPartners(d.data as Partner[]);
      }
    }
    if (guaranteesRes.status === "fulfilled" && guaranteesRes.value.ok) {
      const d = await guaranteesRes.value.json().catch(() => null);
      if (d?.code === 200) {
        const list = Array.isArray(d.data)
          ? d.data
          : Array.isArray(d.data?.list)
          ? d.data.list
          : null;
        if (list && list.length > 0) {
          setCachedGuarantees(list as Guarantee[]);
        }
      }
    }
  } catch {
    // 静默失败
  }
}

// ---- 从本地缓存查询 ----
function verifyFromCache(id: string): VerifyResult | null {
  const partners = getCachedPartners() ?? [];
  const guarantees = getCachedGuarantees() ?? [];

  const g = guarantees.find(
    item => item.guarantee_id.toLowerCase() === id.toLowerCase() && item.status === 1
  );
  if (g) {
    return {
      status: "success",
      source: "cache",
      message: "验证通过！该ID为官方认证合作伙伴，放心合作。",
      data: { id: g.guarantee_id, name: g.name, remark: g.remark },
    };
  }

  const p = partners.find(
    item => item.handle.toLowerCase() === id.toLowerCase() && item.status === 1
  );
  if (p) {
    return {
      status: "success",
      source: "cache",
      message: "验证通过！该ID为官方认证合作伙伴，放心合作。",
      data: {
        id: p.handle,
        name: p.name,
        remark: `${p.name} - 官方认证商务人员`,
      },
    };
  }

  // 缓存里有数据但没找到 → 明确失败
  if (partners.length > 0 || guarantees.length > 0) {
    return {
      status: "fail",
      source: "cache",
      message: "验证失败！该ID不在官方认证列表中，请谨慎辨别，防止被骗。",
    };
  }

  return null;
}

/**
 * 预热缓存（页面加载时调用）
 */
export async function warmupVerifyCache(): Promise<void> {
  await refreshCache();
}

/**
 * 担保验证 Hook
 *
 * 验证流程：
 * 1. 优先直接调 API 验证（最权威，8秒超时）
 * 2. API 成功 → 直接显示结果，后台刷新缓存
 * 3. API 失败（超时/网络错误）→ 尝试从本地缓存查询
 * 4. 缓存也没有 → 尝试拉取最新列表再查一次
 * 5. 全部失败 → 显示网络错误提示
 */
export function useGuaranteeVerify() {
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [loading, setLoading] = useState(false);

  const verify = useCallback(async (id: string) => {
    const trimId = id.trim();
    if (!trimId) return;

    setLoading(true);
    setResult(null);

    try {
      // 第一步：直接调 API 验证（最权威）
      const apiResult = await verifyViaAPI(trimId);

      if (apiResult) {
        // API 有明确结果（成功或失败），直接显示，不走缓存
        setResult(apiResult);
        // 后台静默刷新缓存
        refreshCache();
        return;
      }

      // 第二步：API 超时/网络错误 → 降级到本地缓存
      const cacheResult = verifyFromCache(trimId);
      if (cacheResult) {
        setResult(cacheResult);
        return;
      }

      // 第三步：缓存也没数据 → 拉取最新列表再查一次
      await refreshCache();
      const retryResult = verifyFromCache(trimId);
      if (retryResult) {
        setResult(retryResult);
      } else {
        setResult({
          status: "error",
          source: "cache",
          message: "网络连接失败，请稍后重试或联系客服确认。",
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setLoading(false);
  }, []);

  return { result, loading, verify, reset };
}
